import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-12 text-slate-800">
      <Link to="/" className="text-emerald-600 font-medium hover:underline">← Retour à l'accueil</Link>
      <h1 className="text-3xl font-bold mt-6 mb-2">Politique de confidentialité</h1>
      <p className="text-slate-500 mb-8">Dernière mise à jour : février 2026</p>

      <section className="space-y-6 leading-relaxed">
        <div>
          <h2 className="text-xl font-semibold mb-2">1. Responsable du traitement</h2>
          <p>
            La plateforme DoctoDays (ci-après « DoctoDays », « nous ») est éditée par son exploitant
            dont les coordonnées sont disponibles sur la page <Link to="/terms" className="text-emerald-600 hover:underline">Conditions
            d'utilisation</Link>. DoctoDays agit en qualité de responsable du traitement au sens du
            Règlement (UE) 2016/679 (« RGPD »).
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">2. Données collectées</h2>
          <p>Nous collectons les catégories de données suivantes :</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>Identification</strong> : nom, prénom, adresse email, téléphone.</li>
            <li><strong>Profil praticien</strong> : spécialité, adresse du cabinet, tarif, numéro RPPS (optionnel), photo de profil.</li>
            <li><strong>Rendez-vous</strong> : date, heure, motif libre renseigné par le patient.</li>
            <li><strong>Avis</strong> : note de 1 à 5, commentaire libre.</li>
            <li><strong>Données techniques</strong> : adresse IP, type d'appareil, logs serveur (conservés 12 mois).</li>
          </ul>
          <p className="mt-2">Le motif de consultation saisi par le patient peut contenir des <strong>données de santé</strong>. Ces données sont traitées avec un niveau de sécurité renforcé et ne sont accessibles qu'au patient et au praticien concerné.</p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">3. Finalités et bases légales</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>Création et gestion de votre compte — <em>exécution du contrat</em>.</li>
            <li>Prise de rendez-vous et notifications (email de confirmation, rappel 24h) — <em>exécution du contrat</em>.</li>
            <li>Récupération de mot de passe — <em>intérêt légitime (sécurité du compte)</em>.</li>
            <li>Vérification RPPS auprès de l'Annuaire Santé — <em>obligation légale</em>.</li>
            <li>Amélioration du service et statistiques internes (agrégées, anonymisées) — <em>intérêt légitime</em>.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">4. Durée de conservation</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>Compte actif : jusqu'à sa suppression par l'utilisateur.</li>
            <li>Compte inactif pendant 3 ans : suppression automatique.</li>
            <li>Historique des rendez-vous : 5 ans (obligation de traçabilité des actes de soins).</li>
            <li>Emails de récupération : 1 heure (token de reset).</li>
            <li>Logs techniques : 12 mois.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">5. Destinataires</h2>
          <p>Vos données ne sont jamais vendues. Elles sont partagées uniquement avec :</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Le praticien avec lequel vous prenez rendez-vous (nom, email, téléphone, motif).</li>
            <li>Notre hébergeur cloud situé dans l'Union Européenne.</li>
            <li>Notre prestataire d'envoi d'email transactionnel (Resend — USA, avec clauses contractuelles types RGPD).</li>
            <li>L'API publique Annuaire Santé (pour la vérification RPPS uniquement).</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">6. Transferts hors UE</h2>
          <p>
            Certains prestataires (Resend) sont basés aux États-Unis. Ces transferts sont encadrés
            par les Clauses Contractuelles Types adoptées par la Commission Européenne.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">7. Vos droits</h2>
          <p>Conformément au RGPD, vous disposez des droits suivants :</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Droit d'accès, de rectification et d'effacement de vos données.</li>
            <li>Droit à la limitation et à l'opposition du traitement.</li>
            <li>Droit à la portabilité.</li>
            <li>Droit de définir des directives relatives au sort de vos données après votre décès.</li>
            <li>Droit d'introduire une réclamation auprès de la CNIL (<a href="https://www.cnil.fr" className="text-emerald-600 hover:underline">www.cnil.fr</a>).</li>
          </ul>
          <p className="mt-2">Pour exercer ces droits : <strong>privacy@doctodays.com</strong> (DPO : <strong>Simon Yaramis</strong>)</p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">8. Sécurité</h2>
          <p>
            Les mots de passe sont hachés avec bcrypt, les communications sont chiffrées en HTTPS,
            l'authentification repose sur des jetons JWT signés, et les tokens d'application mobile
            sont stockés dans le Keychain (iOS) ou EncryptedSharedPreferences (Android). Les
            données sensibles (motif de RDV, contenu de messagerie) sont chiffrées en base
            de données via AES-128 (Fernet).
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">9. Cookies</h2>
          <p>
            DoctoDays n'utilise que des cookies strictement nécessaires au fonctionnement du service
            (session d'authentification). Aucun cookie publicitaire ni de traçage tiers.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">10. Délégué à la Protection des Données (DPO)</h2>
          <p>
            Conformément à l'article 37 du RGPD, DoctoDays a désigné officiellement
            un Délégué à la Protection des Données : <strong>Simon YARAMIS</strong>.
            Il est joignable à l'adresse <strong>privacy@doctodays.com</strong> pour toute
            question relative à vos données personnelles. Délai de réponse maximum : 1 mois.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">11. Contact</h2>
          <p>
            Pour toute question liée à vos données : <strong>privacy@doctodays.com</strong><br />
            Pour le support : <strong>support@doctodays.com</strong>
          </p>
        </div>
      </section>
    </div>
  );
}
